import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const DeviceTypeForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { get, post, put, loading } = useApi();
  
  const [formData, setFormData] = useState({
    description: '',
    information: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (id) {
      fetchDeviceType();
    }
  }, [id]);

  const fetchDeviceType = async () => {
    const { data } = await get(`/api/device-types/${id}`);
    if (data) {
      setFormData({
        description: data.description || '',
        information: data.information || ''
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.information.trim()) {
      newErrors.information = 'Information is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) return;

    let response;
    if (id) {
      response = await put(`/api/device-types/${id}`, formData);
    } else {
      response = await post('/api/device-types', formData);
    }

    if (!response.error) {
      navigate('/device-types');
    }
  };

  if (loading && id) return <LoadingSpinner />;

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate('/device-types')}
          className="mr-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-6 w-6" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800">
          {id ? 'Edit Device Type' : 'Add New Device Type'}
        </h1>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Description Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={`input-field ${errors.description ? 'border-red-500' : ''}`}
              placeholder="Enter device type description"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          {/* Information Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Information <span className="text-red-500">*</span>
            </label>
            <textarea
              name="information"
              value={formData.information}
              onChange={handleChange}
              rows="4"
              className={`input-field ${errors.information ? 'border-red-500' : ''}`}
              placeholder="Enter device type information"
            />
            {errors.information && (
              <p className="mt-1 text-sm text-red-600">{errors.information}</p>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/device-types')}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : id ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeviceTypeForm;